import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowRight, Loader2 } from 'lucide-react';
import { useLang } from '../i18n';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ContactForm = ({ defaultTopic = 'generale', dark = false, testIdPrefix = 'contact' }) => {
  const { lang, t } = useLang();
  const [form, setForm] = useState({ name: '', email: '', company: '', phone: '', message: '', topic: defaultTopic });
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, { ...form, lang });
      toast.success(t('form.success'));
      setForm({ name: '', email: '', company: '', phone: '', message: '', topic: defaultTopic });
    } catch (err) {
      toast.error(t('form.error'));
    } finally {
      setSending(false);
    }
  };

  const inputCls = `w-full bg-transparent border-b-2 ${dark ? 'border-white/20 text-white placeholder-white/40' : 'border-foreground/20 text-foreground placeholder-foreground/40'} rounded-none px-0 py-3 focus:border-secondary focus:outline-none focus:ring-0 transition-colors duration-300`;
  const labelCls = `block font-display text-xs uppercase tracking-[0.2em] font-bold mb-1 ${dark ? 'text-white/50' : 'text-foreground/50'}`;
  const topics = t('form.topics');

  return (
    <form onSubmit={submit} className="space-y-7" data-testid={`${testIdPrefix}-form`}>
      <div className="grid sm:grid-cols-2 gap-7">
        <div>
          <label className={labelCls} htmlFor={`${testIdPrefix}-name`}>{t('form.name')} *</label>
          <input id={`${testIdPrefix}-name`} data-testid={`${testIdPrefix}-name-input`} required value={form.name} onChange={set('name')} className={inputCls} />
        </div>
        <div>
          <label className={labelCls} htmlFor={`${testIdPrefix}-email`}>{t('form.email')} *</label>
          <input id={`${testIdPrefix}-email`} data-testid={`${testIdPrefix}-email-input`} type="email" required value={form.email} onChange={set('email')} className={inputCls} />
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-7">
        <div>
          <label className={labelCls} htmlFor={`${testIdPrefix}-company`}>{t('form.company')}</label>
          <input id={`${testIdPrefix}-company`} data-testid={`${testIdPrefix}-company-input`} value={form.company} onChange={set('company')} className={inputCls} />
        </div>
        <div>
          <label className={labelCls} htmlFor={`${testIdPrefix}-phone`}>{t('form.phone')}</label>
          <input id={`${testIdPrefix}-phone`} data-testid={`${testIdPrefix}-phone-input`} value={form.phone} onChange={set('phone')} className={inputCls} />
        </div>
      </div>
      <div>
        <label className={labelCls} htmlFor={`${testIdPrefix}-topic`}>{t('form.topic')}</label>
        <select
          id={`${testIdPrefix}-topic`}
          data-testid={`${testIdPrefix}-topic-select`}
          value={form.topic}
          onChange={set('topic')}
          className={`${inputCls} cursor-pointer ${dark ? '[&>option]:text-[#111827]' : ''}`}
        >
          <option value="generale">{topics.generale}</option>
          <option value="private-label">{topics['private-label']}</option>
          <option value="horeca">{topics.horeca}</option>
        </select>
      </div>
      <div>
        <label className={labelCls} htmlFor={`${testIdPrefix}-message`}>{t('form.message')} *</label>
        <textarea id={`${testIdPrefix}-message`} data-testid={`${testIdPrefix}-message-input`} required rows={4} value={form.message} onChange={set('message')} className={`${inputCls} resize-none`} />
      </div>
      <button
        type="submit"
        disabled={sending}
        data-testid={`${testIdPrefix}-submit-button`}
        className="group inline-flex items-center gap-3 bg-secondary text-white px-8 py-4 font-display font-bold text-sm uppercase tracking-wider hover:bg-primary transition-colors duration-300 disabled:opacity-60"
      >
        {sending ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" />
            {t('form.sending')}
          </>
        ) : (
          <>
            {t('form.submit')}
            <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
          </>
        )}
      </button>
    </form>
  );
};

export default ContactForm;
